import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField as MuiTextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  LinearProgress,
  Snackbar
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Parse from 'parse';
import CustomPagination from '../../components/CustomPagination';

const PAGE_SIZE = 10;

const today = () => new Date().toISOString().split('T')[0];

// Daily-coins amounts are decimal currency units (summed raw bet/win), NOT cents.
const formatAmt = (val) => (val != null ? Number(val).toFixed(2) : '0.00');

const aggregateByGame = (rows) => {
  const map = new Map();
  rows.forEach((r) => {
    const key = r.gameName || 'Unknown';
    if (!map.has(key)) {
      map.set(key, { gameName: key, totalBetAmount: 0, totalWinAmount: 0, sessionCount: 0 });
    }
    const g = map.get(key);
    g.totalBetAmount += r.totalBetAmount || 0;
    g.totalWinAmount += r.totalWinAmount || 0;
    g.sessionCount += r.sessionCount || 0;
  });
  return Array.from(map.values()).map((g) => ({
    ...g,
    netAmount: g.totalBetAmount - g.totalWinAmount,
    rtp: g.totalBetAmount === 0 ? 0 : (g.totalWinAmount / g.totalBetAmount) * 100
  }));
};

const SummaryCard = ({ label, value, color }) => (
  <Box
    sx={{
      backgroundColor: '#1A1A1A',
      borderRadius: 2,
      p: 2,
      minWidth: 140,
      flex: '1 1 140px'
    }}
  >
    <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, mb: 0.5 }}>{label}</Typography>
    <Typography sx={{ color: color || '#fff', fontSize: 20, fontWeight: 700 }}>{value}</Typography>
  </Box>
);

const AgentCurrencyReport = () => {
  const [games, setGames] = useState([]);
  const [gameNameFilter, setGameNameFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  // Per-game lifetime rows (from OperatorGameStat) for the no-date-filter table.
  const [lifetimeGames, setLifetimeGames] = useState([]);

  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [showExportSnackbar, setShowExportSnackbar] = useState(false);

  const todayStr = today();

  // Load the operator's games for the Game / Game Type dropdowns and to resolve
  // each game's category (Game Type) by name.
  useEffect(() => {
    const loadGames = async () => {
      try {
        const sessionToken = localStorage.getItem('sessionToken');
        const result = await Parse.Cloud.run('rgsListGamesForDropdown', {}, { sessionToken });
        if (result?.games) setGames(result.games);
      } catch {
        setGames([]);
      }
    };
    loadGames();
  }, []);

  const callApi = useCallback(async (filterOverride) => {
    const sessionToken = localStorage.getItem('sessionToken');
    const operatorId = localStorage.getItem('operatorId');
    const filter = filterOverride || {};
    const result = await Parse.Cloud.run(
      'getDailyOperatorCoins',
      { page: 1, perPage: 5000, operatorId, field: 'gameName', order: 'ASC', filter },
      { sessionToken }
    );
    return result.data || [];
  }, []);

  // Load lifetime summary on mount from the pre-aggregated OperatorGameStat
  // (instant) instead of scanning a year of PlayHistory.
  useEffect(() => {
    const loadSummary = async () => {
      setSummaryLoading(true);
      try {
        const sessionToken = localStorage.getItem('sessionToken');
        const result = await Parse.Cloud.run('getOperatorLifetimeSummary', {}, { sessionToken });
        setSummary(result?.summary || null);
        setLifetimeGames(result?.games || []);
      } catch (err) {
        console.error('Summary load error:', err);
        setSummary(null);
      } finally {
        setSummaryLoading(false);
      }
    };
    loadSummary();
  }, []);

  const handleSearch = async () => {
    setLoading(true);
    setHasSearched(true);
    setPage(1);
    try {
      if (!startDate && !endDate) {
        // Lifetime, game-wise — served instantly from the precomputed
        // OperatorGameStat rows instead of scanning a year of PlayHistory.
        const gn = gameNameFilter.trim().toLowerCase();
        const rows = gn
          ? lifetimeGames.filter((r) => (r.gameName || '').toLowerCase() === gn)
          : lifetimeGames;
        setAllRows(rows);
      } else {
        const filter = {};
        if (startDate) filter.startDate = startDate;
        if (endDate) filter.endDate = endDate;
        if (gameNameFilter.trim()) filter.gameName = gameNameFilter.trim();
        const rows = await callApi(filter);
        setAllRows(aggregateByGame(rows));
      }
    } catch (err) {
      console.error('Search error:', err);
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setGameNameFilter('');
    setStartDate('');
    setEndDate('');
    setAllRows([]);
    setHasSearched(false);
    setPage(1);
  };

  const handleExport = async () => {
    setIsExporting(true);
    setShowExportSnackbar(true);
    try {
      const rows = allRows.map((r, i) => {
        setExportProgress(Math.floor((i / allRows.length) * 80) + 10);
        return {
          'Game Name': r.gameName,
          'Total Bet': formatAmt(r.totalBetAmount),
          'Total Win': formatAmt(r.totalWinAmount),
          'Total Loss': formatAmt(Math.max(0, r.totalWinAmount - r.totalBetAmount)),
          'Net Win': formatAmt(r.totalBetAmount - r.totalWinAmount),
          'RTP %': r.rtp.toFixed(2),
          'Total Spins': r.sessionCount
        };
      });
      setExportProgress(95);
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Agent Currency Report');
      const data = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      setExportProgress(100);
      saveAs(new Blob([data], { type: 'application/octet-stream' }), 'AgentCurrencyReport.xlsx');
      setTimeout(() => setShowExportSnackbar(false), 800);
    } catch (err) {
      alert('Export failed: ' + err.message);
    } finally {
      setIsExporting(false);
    }
  };

  const pagedRows = allRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Box sx={{ mt: 2 }}>
      <Snackbar
        open={showExportSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={null}
      >
        <Alert severity="info" icon={false} sx={{ px: 2 }}>
          <Typography variant="body2" mb={0.5}>
            Exporting... {exportProgress}%
          </Typography>
          <LinearProgress
            variant="determinate"
            value={exportProgress}
            sx={{ width: 260, height: 6, borderRadius: 5 }}
          />
        </Alert>
      </Snackbar>

      {/* Search Filters */}
      <Box sx={{ backgroundColor: '#242424', borderRadius: 2, p: 2.5, mb: 2 }}>
        <Typography sx={{ color: '#FFC107', fontWeight: 600, fontSize: 15, mb: 2 }}>
          Search Filters
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-end">
          <FormControl size="small" sx={{ minWidth: 200, ...selectContainerSx }}>
            <InputLabel shrink sx={{ color: 'rgba(255,255,255,0.6)' }}>
              Game Name
            </InputLabel>
            <Select
              label="Game Name"
              notched
              displayEmpty
              value={gameNameFilter}
              onChange={(e) => setGameNameFilter(e.target.value)}
              sx={selectSx}
            >
              <MenuItem value="">All Games</MenuItem>
              {games.map((g) => (
                <MenuItem key={g.id} value={g.name}>
                  {g.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <MuiTextField
            size="small"
            type="date"
            label="Date From"
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            inputProps={{ max: todayStr }}
            sx={fieldSx}
          />

          <MuiTextField
            size="small"
            type="date"
            label="Date To"
            InputLabelProps={{ shrink: true }}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            inputProps={{ min: startDate || undefined, max: todayStr }}
            sx={fieldSx}
          />

          <Button variant="contained" onClick={handleSearch} disabled={loading} sx={applyBtnSx}>
            {loading ? <CircularProgress size={18} sx={{ color: '#000' }} /> : 'Search'}
          </Button>

          <Button variant="outlined" onClick={handleClear} disabled={loading} sx={clearBtnSx}>
            Clear
          </Button>

          <Button
            variant="contained"
            startIcon={<GetAppIcon />}
            onClick={handleExport}
            disabled={isExporting || !hasSearched || allRows.length === 0}
            sx={applyBtnSx}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Overall Summary */}
      <Box sx={{ backgroundColor: '#242424', borderRadius: 2, p: 2.5, mb: 2 }}>
        <Typography sx={{ color: '#FFC107', fontWeight: 600, fontSize: 15, mb: 2 }}>
          Overall Summary
        </Typography>
        {summaryLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress sx={{ color: '#FFC107' }} />
          </Box>
        ) : !summary ? (
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>
            No lifetime data available.
          </Typography>
        ) : (
          <Box display="flex" flexWrap="wrap" gap={2}>
            <SummaryCard label="Total Bet" value={formatAmt(summary.totalBetAmount)} color="#fff" />
            <SummaryCard
              label="Total Win"
              value={formatAmt(summary.totalWinAmount)}
              color="#4caf50"
            />
            <SummaryCard label="Total Loss" value={formatAmt(summary.totalLoss)} color="#f44336" />
            <SummaryCard
              label="Net Win"
              value={formatAmt(summary.netWin)}
              color={summary.netWin >= 0 ? '#4caf50' : '#f44336'}
            />
            <SummaryCard label="RTP" value={`${summary.rtp.toFixed(2)}%`} color="#FFC107" />
            <SummaryCard label="Total Spins" value={summary.sessionCount} color="#29b6f6" />
          </Box>
        )}
      </Box>

      {/* Report Table */}
      <Box sx={{ backgroundColor: '#242424', borderRadius: 2, p: 2 }}>
        <Typography sx={{ color: '#FFC107', fontWeight: 600, fontSize: 15, mb: 2 }}>
          Report Table
        </Typography>
        <TableContainer component={Paper} sx={{ backgroundColor: '#1A1A1A', mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#2A2A2A' }}>
                {[
                  'Game Name',
                  'Total Bet',
                  'Total Win',
                  'Total Loss',
                  'Net Win',
                  'RTP %',
                  'Total Spins'
                ].map((col, i) => (
                  <TableCell
                    key={col}
                    align={i === 0 ? 'left' : 'right'}
                    sx={{ color: '#FFC107', fontWeight: 600, py: 1.5 }}
                  >
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress sx={{ color: '#FFC107' }} />
                  </TableCell>
                </TableRow>
              ) : pagedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#fff' }}>
                    {hasSearched
                      ? 'No data found for the selected filters.'
                      : 'Apply filters and click Search to view the report.'}
                  </TableCell>
                </TableRow>
              ) : (
                pagedRows.map((row) => {
                  const netWin = row.totalBetAmount - row.totalWinAmount;
                  const totalLoss = Math.max(0, row.totalWinAmount - row.totalBetAmount);
                  return (
                    <TableRow key={row.gameName} sx={{ '&:hover': { backgroundColor: '#2A2A2A' } }}>
                      <TableCell sx={{ color: '#fff', fontWeight: 500 }}>{row.gameName}</TableCell>
                      <TableCell align="right" sx={{ color: '#fff' }}>
                        {formatAmt(row.totalBetAmount)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#4caf50' }}>
                        {formatAmt(row.totalWinAmount)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: totalLoss > 0 ? '#f44336' : 'rgba(255,255,255,0.5)' }}
                      >
                        {formatAmt(totalLoss)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: netWin >= 0 ? '#4caf50' : '#f44336' }}>
                        {formatAmt(netWin)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#FFC107' }}>
                        {row.rtp.toFixed(2)}%
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#fff' }}>
                        {row.sessionCount}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {allRows.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CustomPagination
              page={page}
              perPage={PAGE_SIZE}
              total={allRows.length}
              setPage={setPage}
              setPerPage={() => {}}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

const fieldSx = {
  '& .MuiOutlinedInput-root': { color: '#fff' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#FFC107' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#FFC107'
  }
};

const selectContainerSx = {
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#FFC107' }
};

const selectSx = {
  color: '#fff',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FFC107' },
  '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.6)' }
};

const applyBtnSx = {
  background: '#FFC107',
  color: '#0A0A0A',
  textTransform: 'none',
  fontWeight: 600,
  '&:hover': { backgroundColor: '#FFA000' },
  '&:disabled': { backgroundColor: 'rgba(255,193,7,0.3)', color: 'rgba(0,0,0,0.4)' }
};

const clearBtnSx = {
  borderColor: '#FFC107',
  color: '#FFC107',
  textTransform: 'none',
  '&:hover': { borderColor: '#FFA000', color: '#FFA000', backgroundColor: 'rgba(255,193,7,0.08)' }
};

export default AgentCurrencyReport;
